/**
 * Minimal placeholder client bundle for the Gist web app.
 *
 * This file serves as the entry point for client-side JavaScript in the Gist application.
 * Currently contains placeholder implementations that log to console. Once the front-end
 * is built (React/Vue/Svelte), this will be replaced with actual SPA initialization.
 *
 * Architecture:
 * - Bundled by build.js via esbuild
 * - Loaded by SSR templates as <script type="module" src="/static/app.js">
 * - Auto-registers functions on globalThis for SSR inline scripts to call
 * - Provides hydration hooks for progressive enhancement
 *
 * Built by:
 * - build.js script (bundles this file with esbuild)
 * - Output: dist/client/app.js
 *
 * Loaded by:
 * - src/ssr/app.ts HTML shell (<script type="module">)
 * - src/ssr/profile.ts for client-side profile enhancement
 * - src/ssr/share.ts for share page interactivity (future)
 *
 * Current capabilities:
 * - Placeholder bootstrap logging (bootstrapApp)
 * - Profile enhancement hooks (enhanceProfile) - no-op
 * - Global registration for SSR script access
 *
 * Future expansion:
 * - Replace bootstrapApp() with React/Vue/Svelte initialization
 * - Add actual client-side routing (React Router, Vue Router, etc.)
 * - Implement real profile interactivity (follow buttons, content loading)
 * - Add state management (Redux, Zustand, Pinia, etc.)
 * - Implement progressive enhancement for all SSR pages
 *
 * Design philosophy:
 * - Start minimal, expand incrementally
 * - SSR-first with client enhancement
 * - No framework lock-in (easy to replace placeholders)
 * - Type-safe contracts between SSR and client
 */

import { createWebAuthBridge } from './web-auth-bridge.js';

/**
 * Configuration object passed from SSR to client for hydration.
 *
 * Server-rendered pages inject inline scripts that call client functions with this config.
 * Allows SSR to pass initial state to client without full page data re-fetch.
 *
 * Used by:
 * - enhanceProfile() to know which profile is being viewed
 * - Future: bootstrapApp() for initial app state
 *
 * Current fields:
 * - username: Profile username for /u/:username pages
 *
 * Future fields:
 * - initialData: Pre-fetched content to avoid client fetch
 * - userSession: Current user info if authenticated
 * - featureFlags: A/B test variations
 */
interface HydrationConfig {
  /** Username of the profile being viewed (for profile pages) */
  username?: string;
}

/**
 * Global type augmentation to expose hydration functions to SSR inline scripts.
 *
 * Why this exists:
 * - SSR pages (src/ssr/profile.ts) inject inline <script> tags in HTML
 * - Those inline scripts call functions like enhanceProfile() or bootstrapApp()
 * - TypeScript needs to know these functions exist on window/globalThis
 * - This declaration tells TypeScript they're optional properties on Window
 * - The actual assignment happens at bottom of file via Object.assign()
 *
 * Pattern:
 * - Server HTML: <script>if (window.enhanceProfile) window.enhanceProfile({ username: 'alice' })</script>
 * - Client: This module auto-runs on load and assigns to globalThis
 * - Result: SSR can safely call client functions without runtime errors
 *
 * Why optional (? modifier):
 * - Functions only exist after this module loads
 * - SSR inline scripts run immediately, may execute before module load
 * - Inline scripts check for existence before calling: if (window.enhanceProfile)
 * - Prevents errors if script execution order is unexpected
 *
 * Future:
 * - Add gistHydrate umbrella function for all hydration
 * - Add bootstrapApp once SPA framework is chosen
 * - Add event bus for SSR ↔ client communication
 */
declare global {
  interface Window {
    /** Profile enhancement function (called by src/ssr/profile.ts inline scripts) */
    enhanceProfile?: (config: HydrationConfig) => void;

    /** App bootstrap function (called by src/ssr/app.ts inline scripts) */
    bootstrapApp?: () => void;
  }
}

/**
 * Placeholder bootstrap function for SPA initialization.
 *
 * Currently just logs to console. When a front-end framework is added,
 * this will initialize the full SPA (React, Vue, Svelte, etc.).
 *
 * Called by:
 * - src/ssr/app.ts when loading /app shell
 * - SSR inline script: <script>if (window.bootstrapApp) window.bootstrapApp()</script>
 *
 * Current behavior:
 * - Logs to console (debugging aid to confirm client bundle loaded)
 *
 * Future implementation (React example):
 * ```typescript
 * export function bootstrapApp(): void {
 *   const root = document.getElementById('app');
 *   if (!root) throw new Error('App root not found');
 *
 *   const reactRoot = createRoot(root);
 *   reactRoot.render(<App />);
 * }
 * ```
 *
 * Future implementation (Vue example):
 * ```typescript
 * export function bootstrapApp(): void {
 *   const app = createApp(App);
 *   app.mount('#app');
 * }
 * ```
 *
 * Execution context:
 * - Runs in browser after page load
 * - Has access to full DOM
 * - Can fetch data, set up routing, etc.
 * - Auto-registered on globalThis (see bottom of file)
 *
 * @returns void
 */
export function bootstrapApp(): void {
  // Initialize the web ↔ extension auth bridge early so pages can react to auth state.
  // This is a no-op if the extension isn't installed.
  createWebAuthBridge().listen();

  // Placeholder bootstrap that can be expanded later.
  console.log('Gist web app bootstrap executed');
}

/**
 * Enhances server-rendered profile page with client-side interactivity.
 *
 * Currently just logs to console. When profile interactivity is built,
 * this will add follow buttons, infinite scroll, content filtering, etc.
 *
 * Called by:
 * - src/ssr/profile.ts inline script on /u/:username pages
 * - SSR passes username via config parameter
 *
 * Current behavior:
 * - Logs username to console (debugging aid)
 *
 * Future implementation:
 * ```typescript
 * export function enhanceProfile(config: HydrationConfig): void {
 *   const { username } = config;
 *
 *   // Set up follow button
 *   const followBtn = document.querySelector('[data-follow-button]');
 *   followBtn?.addEventListener('click', () => followUser(username));
 *
 *   // Set up infinite scroll
 *   const contentContainer = document.querySelector('[data-content-list]');
 *   observeInfiniteScroll(contentContainer, username);
 *
 *   // Set up real-time updates
 *   subscribeToProfileUpdates(username);
 * }
 * ```
 *
 * Progressive enhancement strategy:
 * - Server renders static profile HTML
 * - Client enhances with interactivity
 * - Works without JavaScript (SSR content visible)
 * - Enhanced experience when JavaScript loads
 *
 * Execution context:
 * - Runs after SSR content is in DOM
 * - Can safely query for SSR-rendered elements
 * - Auto-registered on globalThis (see bottom of file)
 *
 * @param config - Hydration config with username and future state
 * @returns void
 */
export function enhanceProfile(config: HydrationConfig): void {
  console.log('Enhancing profile view for', config.username);
}

/**
 * Auto-register hydration functions on globalThis for SSR scripts.
 *
 * Why this is needed:
 * - SSR templates inject inline <script> tags that call these functions
 * - Inline scripts execute in global scope (not ES modules)
 * - This assignment makes functions available globally
 * - Enables SSR → client communication without bundler complexity
 *
 * Execution:
 * - Runs immediately when module loads
 * - typeof guard prevents errors in non-browser environments (tests, SSR)
 * - Object.assign merges functions onto globalThis without overwriting
 *
 * Security note:
 * - Only exposes intended public functions
 * - No sensitive data or internal APIs exposed
 * - SSR inline scripts are trusted (we control the HTML)
 *
 * Alternative approaches considered:
 * - Custom events: Too complex for simple function calls
 * - PostMessage: Overkill for same-origin communication
 * - Data attributes: Works for data, not functions
 * - Global namespace object: More verbose, same result
 * - Chosen: Direct assignment (simplest, most explicit)
 */
if (typeof globalThis !== 'undefined') {
  Object.assign(globalThis, { enhanceProfile, bootstrapApp });
}
